import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchNlpReviewTableData, fetchReviewTableData } from './reviewTableAPI';
import { RootState } from '../../../../state/store';
import { setNoOfInterventions } from '../../../../components/FormComponent/formSlice';

export interface ReviewTableState {
  mappings: Record<string, any>[];
  isNlpResult: boolean;
  loading: boolean;
  error: string | null;
  enableReviewTableBtn: boolean;
  enableReviewTableDialog: boolean;
}

const initialState: ReviewTableState = {
  mappings: [],
  isNlpResult: false,
  loading: false,
  error: null,
  enableReviewTableBtn: false,
  enableReviewTableDialog: false,
};

export const fetchReviewTableDataThunk = createAsyncThunk(
  'reviewTable/fetchReviewTableData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchReviewTableData(payload);
      dispatch(setNoOfInterventions(response.length));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  },
);

export const fetchNLPReviewTableDataThunk = createAsyncThunk(
  'reviewTable/fetchNLPReviewTableData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchNlpReviewTableData(payload);
      dispatch(setNoOfInterventions(response.length));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  },
);

const reviewTableSlice = createSlice({
  name: 'reviewTable',
  initialState,
  reducers: {
    resetReviewTable: (state) => {
      state.mappings = [];
      state.error = null;
    },
    setReviewTableMappings: (state, action: PayloadAction<any>) => {
      state.mappings = action.payload;
    },
    setNlpResultTrue: (state) => {
      state.isNlpResult = true;
    },
    setNlpResultFalse: (state) => {
      state.isNlpResult = false;
    },
    toggleEnableReviewTableBtn: (state, action: PayloadAction<boolean>) => {
      state.enableReviewTableBtn = action.payload;
    },
    toggleEnableReviewTableDialog: (state, action: PayloadAction<boolean>) => {
      state.enableReviewTableDialog = action.payload;
    },
    editInputMappingRow: (
      state,
      action: PayloadAction<{
        rowIndex: number;
        updatedRow: Record<string, any>;
      }>,
    ) => {
      const { rowIndex, updatedRow } = action.payload;
      state.mappings[rowIndex] = updatedRow;
    },
    deleteInputMappingRow: (state, action: PayloadAction<number[]>) => {
      const indicesToDelete = new Set(action.payload);
      state.mappings = state.mappings.filter((_, index) => !indicesToDelete.has(index));
    },
  },
  extraReducers: (builder) => {
    const commonPending = (state: ReviewTableState) => {
      state.loading = true;
      state.error = null;
      state.mappings = [];
    };

    const commonFulfilled = (state: ReviewTableState, action: PayloadAction<any>) => {
      state.loading = false;
      state.mappings = action.payload;
    };

    const commonRejected = (state: ReviewTableState, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchReviewTableDataThunk.pending, commonPending)
      .addCase(fetchReviewTableDataThunk.fulfilled, commonFulfilled)
      .addCase(fetchReviewTableDataThunk.rejected, commonRejected)
      .addCase(fetchNLPReviewTableDataThunk.pending, commonPending)
      .addCase(fetchNLPReviewTableDataThunk.fulfilled, commonFulfilled)
      .addCase(fetchNLPReviewTableDataThunk.rejected, commonRejected);
  },
});

export const {
  resetReviewTable,
  setNlpResultTrue,
  setNlpResultFalse,
  toggleEnableReviewTableBtn,
  toggleEnableReviewTableDialog,
  editInputMappingRow,
  deleteInputMappingRow,
  setReviewTableMappings,
} = reviewTableSlice.actions;

export const selectReviewTableMappings = (state: RootState) => state.reviewTable.mappings;
export const selectReviewTableLoading = (state: RootState) => state.reviewTable.loading;
export const selectReviewTableError = (state: RootState) => state.reviewTable.error;
export const selectEnableReviewTableBtn = (state: RootState) => state.reviewTable.enableReviewTableBtn;
export const selectEnableReviewTableDialog = (state: RootState) => state.reviewTable.enableReviewTableDialog;

export default reviewTableSlice.reducer;
